import * as Yup from 'yup';
import React, { useState, useRef, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Typography,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { makeStyles, styled } from '@mui/styles';
import { Box } from '@mui/system';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import FileBase64 from 'react-file-base64';
import { CircleSpinner } from 'react-spinners-kit';
import secureLocalStorage from 'react-secure-storage';
import { ToastContainer, toast } from 'material-react-toastify';
import 'material-react-toastify/dist/ReactToastify.css';

const useStyles = makeStyles((theme) => ({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '30px',
    backgroundColor: 'rgba(0, 0, 0, .2)'
  },
  button: {
    borderRadius: '25px',
    backgroundColor: '#00AB55',
    color: 'white',
    paddingLeft: '10px',
    paddingRight: '10px',
    fontSize: '13px',
    textTransform: 'none',
    boxShadow: '0 8px 16px 0 rgb(0 171 85 / 24%)',
    '&:hover': {
      backgroundColor: '#1edb7c'
    }
  },
  headerContainer: {
    position: 'relative',
    height: '100px'
  },
  header: {
    display: 'flex',
    position: 'absolute',
    width: 'calc(100%)',
    top: '-70px',
    alignItems: 'flex-end',
    '& > *': {
      margin: '24px 8px'
    }
  },
  spacer: {
    flexGrow: '1'
  },
  avatar: {
    border: `3px solid white`,
    width: 80,
    height: 80
  },
  actionGroup: {
    display: 'flex',
    width: '330px',
    justifyContent: 'space-between',
    marginRight: 0
  },
  summaryCards: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  summaryCard: {
    margin: 8,
    flexGrow: 1,
    padding: 24
  },
  tripCard: {
    margin: 8,
    padding: 16
  },
  input: {
    display: 'none'
  },
  large: {
    width: 100,
    height: 100
  },
  bottomBar: {
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    '@media (max-width:690px)': {
      flexDirection: 'column',
      justifyContent: 'center'
    }
  }
}));

const CssTextField = styled(TextField)({
  '& label.Mui-focused': {
    color: '#ec8181'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#ec8181'
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'lightgray'
    },
    '&:hover fieldset': {
      borderColor: '#ec8181'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#ec8181'
    }
  }
});

export default function AddnotificationForm() {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const navigate = useNavigate();
  const [share, setShare] = useState('public');
  const [iconShare, setIconShare] = useState('mdi:earth');
  const [anchor, setAnchor] = useState(null);
  const [spiner, setSpiner] = useState(false);
  const isArabic = /[\u0600-\u06FF\u0750-\u077F]/;

  const notifySuccess = (message) => {
    toast.success(message, {
      position: 'bottom-right',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    });
  };
  const hiddenFileInput = React.useRef(null);
  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const isMenuOpen = Boolean(anchor);
  const openMenu = (event) => {
    setAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchor(null);
  };

  const userDetails = JSON.parse(localStorage.getItem('user'));
  const API_ENDPOINT = process.env.REACT_APP_BASE_URL;
  const formik = useFormik({
    initialValues: {
      created_by: userDetails.user_id,
      title: '',
      text: '',
      share_with: share
    },
    onSubmit: (values) => {
      setSpiner(true);
      const formData = new FormData();
      formData.append('created_by', userDetails.user_id);
      formData.append('title', values.text);
      formData.append('text', values.text);
      formData.append('share_with', share);
      const token = localStorage.getItem('access');
      console.log('data', values);
      axios({
        method: 'post',
        url: `${API_ENDPOINT}/api/notifications/`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        data: formData
      })
        .then((response) => {
          setSpiner(false);
          console.log('status: ', response.status);
          console.log('response: ', response.data);
          notifySuccess(response.data.Message);
          navigate('/dashboard');
        })
        .catch((err) => {
          setSpiner(false);
          console.log('err', err);
        });
    },
    onReset: (values) => {
      values = '';
    }
  });

  const { errors, touched, values, isSubmitting, getFieldProps, setFieldValue } = formik;

  return (
    <FormikProvider value={formik}>
      {spiner && (
        <Box display="flex" justifyContent="center" sx={{ p: 3 }}>
          <CircleSpinner size={30} color="#ec8181" loading={spiner} />
        </Box>
      )}
      <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
        <ToastContainer />
        <Stack spacing={3}>
          <CssTextField
            id="title"
            name="title"
            fullWidth
            autoComplete="title"
            multiline
            rows={1}
            type="input"
            label=""
            style={{ backgroundColor: '#F5F5F5' }}
            placeholder={t('title')}
            {...getFieldProps('title')}
          />
          <CssTextField
            id="text"
            name="text"
            fullWidth
            autoComplete="text"
            multiline
            rows={5}
            type="text"
            label=""
            style={{ backgroundColor: '#F5F5F5' }}
            placeholder={t('message')}
            {...getFieldProps('text')}
          />
        </Stack>
        <Stack className={classes.bottomBar}>
          <Box
            style={{
              alignItems: 'center',
              justifyContent: 'space-around',
              margin: '15px 0px'
            }}
          >
            <Typography variant="h8" style={{ marginRight: '20px', color: 'grey' }}>
              {t('Partageravec')}
            </Typography>
            <Button
              style={{
                maxWidth: '100px',
                marginRight: '20px',
                backgroundColor: '#ebf9f1',
                color: 'grey'
              }}
              variant="contained"
              startIcon={
                <Icon icon={iconShare} width={20} style={{ marginRight: '10px', color: 'grey' }} />
              }
              onClick={openMenu}
            >
              {share}
            </Button>
          </Box>
          <Box
            style={{
              alignItems: 'center',
              justifyContent: 'space-around',
              margin: '15px 0px'
            }}
          >
            <Button
              style={{
                maxWidth: '100px',
                marginRight: document.body.dir === 'ltr' && '20px',
                marginLeft: document.body.dir === 'rtl' && '20px',
                backgroundColor: 'grey'
              }}
              type="button"
              variant="contained"
              loading={isSubmitting}
              onClick={formik.handleReset}
            >
              {t('Annuler')}
            </Button>
            <Button
              style={{
                maxWidth: '100px',
                backgroundColor: '#ec8181',
                color: values.text === '' ? 'lightgrey' : 'white'
              }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={values.text === ''}
            >
              {t('publier')}
            </Button>
          </Box>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
